import { ReactComponent as WorkIcon } from '@assets/icons/profile-icons/work-icon.svg';
import AgoraDatePicker from '@components/V3/Utils/InputsV3/AgoraDatePicker';
import Input from '@components/V4/Inputs/Input';
import TextArea from '@components/V4/Inputs/Textarea';
import Select from '@components/V4/Select/Select';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSaveUserProfile } from '@actions';
import { useAnalytics } from 'apps/agora/src/contexts/AnalyticsContext';
import useToast from 'apps/agora/src/hooks/useToast';
import { ANALYTICS_EVENT_NAMES } from 'apps/agora/src/utils/constants';
import { getNumericEnumValues } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useContext, useEffect } from 'react';
import { useController, useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { ProfileContext } from '../../../AccountProfile';
import { ExamFormProps } from '../../../types';
import StudentFormWrapper from '../../ProfileFormWrapper';
import { Experience } from '@shared/common';
import { ACTIVITY_TYPE, EMPLOYMENT_TYPE, EXPERIENCE_TYPE } from '@shared/constants';
import { ExperienceType, EmploymentType, ActivityType } from '@shared/enums';
import Checkbox from 'antd/lib/checkbox';

interface ResumeTabWorkFormProps extends ExamFormProps {
  jobData?: Experience;
}
const formDataSchema = z
  .object({
    positionTitle: z.string().min(1, { message: 'Please enter a valid school name.' }),
    employer: z.string().min(1, { message: 'Please enter a valid name.' }),
    experienceType: z
      .number()
      .min(0)
      .refine((value) => value in ExperienceType, {
        message: 'Please enter a valid experience type.',
      }),
    employmentType: z
      .number()
      .min(0)
      .refine((value) => value in EmploymentType || value in ACTIVITY_TYPE, {
        message: 'Please enter a valid employment type.',
      }),
    startDate: z.string().min(1, { message: 'Please enter a valid date.' }),
    endDate: z.string().optional().nullable(),
    ongoing: z.boolean().optional().default(false),
    description: z.string().optional().default(''),
  })
  .superRefine((val, ctx) => {
    if (!val.ongoing && (!val.endDate || val.endDate.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Please enter a valid date.',
      });
    }
    if (!val.ongoing && val.startDate && val.endDate) {
      if (val.endDate < val.startDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endDate'],
          message: 'End date cannot be before start date.',
        });
      }
    }
  });

type FormData = z.infer<typeof formDataSchema>;

const ResumeTabWorkForm = (props: ResumeTabWorkFormProps) => {
  const { isModeAddNew, userId, jobData, setIsEditMode, setIsAddNewItem, onCancel } = props;

  const { isExternalViewer } = useContext(ProfileContext);

  const toast = useToast();
  const query = useQueryClient();
  const { trackEvent } = useAnalytics();

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    control,
    getValues,
    reset,
    resetField,
    formState: { errors, touchedFields },
  } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: {
      positionTitle: jobData?.positionTitle,
      employer: jobData?.employer ?? jobData?.institution,
      employmentType: jobData?.employmentType ?? jobData?.activityType,
      startDate: jobData?.startDate,
      endDate: jobData?.endDate,
      description: jobData?.description,
      ongoing: jobData?.ongoing ?? false,
      experienceType: jobData?.experienceType,
    },
  });

  const { field: startDateField, fieldState: startDateFieldState } = useController({
    name: 'startDate',
    control,
  });

  const { field: experienceTypeField, fieldState: experienceTypeFieldState } = useController({
    name: 'experienceType',
    control,
  });

  const experienceTypeValue = useWatch({ control, name: 'experienceType' });

  const { field: endDateField, fieldState: endDateFieldState } = useController({
    name: 'endDate',
    control,
  });

  const { field: employmentTypeField, fieldState: employmentTypeFieldState } = useController({
    name: 'employmentType',
    control,
  });

  const { field: ongoingField } = useController({
    name: 'ongoing',
    control,
  });

  const editButtonHandler = () => {
    setIsEditMode?.(false);
    setIsAddNewItem?.(false);
    reset();
  };

  const { mutate: updateUserProfile, isLoading } = useSaveUserProfile(userId, {
    onSuccess: async () => {
      trackEvent(
        isExternalViewer
          ? ANALYTICS_EVENT_NAMES.STUDENT_PROFILE_CHANGE_BY_MENTOR
          : ANALYTICS_EVENT_NAMES.PROFILE_CHANGE
      );

      toast.success('Successfully updated your profile.');
      await query.invalidateQueries('/users/:id/profile');
      editButtonHandler();
    },
    onError: () => {
      toast.error('Profile could not be saved.');
    },
  });

  const onSubmit = async (data: FormData) => {
    const isValid = await trigger();
    if (isValid) {
      let experience: any = {
        _id: jobData?._id,
        positionTitle: data.positionTitle,
        startDate: data.startDate,
        endDate: data.endDate,
        description: data.description ?? '',
        ongoing: data.ongoing ?? false,
        experienceType: data.experienceType,
      };

      if (data.experienceType === ExperienceType.Work) {
        experience = {
          ...experience,
          employer: data.employer,
          employmentType: data.employmentType,
        };
      } else {
        experience = {
          ...experience,
          institution: data.employer,
          activityType: data.employmentType,
        };
      }
      updateUserProfile({
        experiences: [experience],
      });
    }
  };

  const touchAllFields = (fields: Partial<FormData>) => {
    (Object.keys(fields) as (keyof FormData)[]).forEach((key) => {
      const val = getValues(key);
      setValue(key, val as any, {
        shouldTouch: true,
        shouldValidate: true,
      });
    });
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    const formData = getValues();

    touchAllFields(formData);

    handleSubmit(onSubmit)(e);
  };

  useEffect(() => {
    if (!isModeAddNew) {
      const formData = getValues();

      touchAllFields(formData);
    }
  }, []);

  const workTypeOptions = {
    Work: {
      options: getNumericEnumValues(EmploymentType)?.map((employment) => ({
        label: EMPLOYMENT_TYPE[employment],
        value: employment,
      })),

      label: 'Employment Type',
    },
    Extra: {
      options: getNumericEnumValues(ActivityType)?.map((activity) => ({
        label: ACTIVITY_TYPE[activity],
        value: activity,
      })),

      label: 'Activity Type',
    },
  };

  const currentType = experienceTypeField.value === ExperienceType.Work ? 'Work' : 'Extra';
  const { options: currentOptions, label: currentLabel } = workTypeOptions[currentType];

  const experienceTypeFieldSelectedHandler = (value: number) => {
    if (value !== experienceTypeValue) {
      //TODO find a permanent solution for this
      resetField('employmentType', { defaultValue: null as unknown as number });
    }
    experienceTypeField.onChange(value);
  };

  return (
    <StudentFormWrapper
      title="Position"
      isModeAddNew={isModeAddNew}
      onCancel={onCancel}
      icon={<WorkIcon className="min-w-5" />}
      isLoading={isLoading}
      editButtonHandler={editButtonHandler}
      submitHandler={submitHandler}
    >
      <Select
        options={getNumericEnumValues(ExperienceType)?.map((option) => ({
          label: EXPERIENCE_TYPE[option],
          value: option,
        }))}
        onSelect={(value) => experienceTypeFieldSelectedHandler(value as number)}
        value={experienceTypeField.value}
        onBlur={experienceTypeField.onBlur}
        isRequired
        label="Experience Type"
        isValid={!experienceTypeFieldState.error}
        isTouched={experienceTypeFieldState.isTouched}
        errorText={experienceTypeFieldState.error?.message}
        size="large"
        allowClear={false}
        autoFocus
      />
      <Select
        options={currentOptions}
        onSelect={(value) => employmentTypeField.onChange(value)}
        value={employmentTypeField.value}
        onBlur={employmentTypeField.onBlur}
        isRequired
        label={currentLabel}
        isValid={!employmentTypeFieldState.error}
        isTouched={employmentTypeFieldState.isTouched}
        errorText={employmentTypeFieldState?.error?.message}
        size="large"
        allowClear={false}
      />

      <Input
        type="text"
        id={'positionTitle'}
        placeholder={''}
        label={'Position Name'}
        isRequired
        isTouched={touchedFields.positionTitle}
        isValid={!errors.positionTitle}
        errorText={errors.positionTitle?.message}
        {...register('positionTitle')}
      />
      <Input
        type="text"
        id={'employer'}
        placeholder={''}
        label={currentType === 'Work' ? 'Employer' : 'Institution/Organisation'}
        isRequired
        isTouched={touchedFields.employer}
        isValid={!errors.employer}
        errorText={errors.employer?.message}
        {...register('employer')}
      />

      <div className="gap-6 laptop:flex">
        <AgoraDatePicker
          value={startDateField.value ? moment(startDateField.value) : undefined}
          onChange={(value) => startDateField.onChange(moment(value).format('YYYY-MM-DD'))}
          onBlur={startDateField.onBlur}
          allowClear={false}
          isRequired
          label="Start Date"
          isValid={!startDateFieldState.error}
          isTouched={startDateFieldState.isTouched}
          errorText={startDateFieldState.error?.message}
        />

        {!ongoingField.value && (
          <AgoraDatePicker
            value={endDateField.value ? moment(endDateField.value) : undefined}
            onChange={(value) => endDateField.onChange(moment(value).format('YYYY-MM-DD'))}
            onBlur={endDateField.onBlur}
            allowClear={false}
            isRequired={!ongoingField.value}
            label="End Date"
            isValid={!endDateFieldState.error}
            isTouched={endDateFieldState.isTouched}
            errorText={endDateFieldState.error?.message}
          />
        )}
      </div>

      <div className="gap-6 laptop:flex">
        <Checkbox
          checked={ongoingField.value}
          onChange={async (e) => {
            const checked = e.target.checked;
            setValue('ongoing', checked, { shouldValidate: true, shouldTouch: true });

            if (checked) {
              setValue('endDate', '', { shouldValidate: true, shouldTouch: false });
            }

            await trigger(['endDate', 'ongoing']);
          }}
          className="text-white m-6"
        >
          This working experience is ongoing
        </Checkbox>
      </div>
      <TextArea
        id={'description'}
        placeholder={''}
        label={'Description'}
        {...register('description')}
      />
    </StudentFormWrapper>
  );
};

export default ResumeTabWorkForm;
